import React from 'react';
import { useStyletron } from 'baseui';
import { Checkbox } from 'baseui/checkbox';
import { Input } from 'baseui/input';

interface DeviceConfigBoxProps {
	title: string;
	platform: string;
	adConfig: any;
	configPath: string[];
	adType: string;
	subPlatform?: string;
	deviceConfig: any;
	isUsingGlobal: boolean;
	onUpdate: (path: string[], value: any) => void;
	createLocalDeviceConfig: (adConfig: any, platform: string, subPlatform: string | undefined, updates: any) => any;
}

const DeviceConfigBox: React.FC<DeviceConfigBoxProps> = ({
	title,
	platform,
	adConfig,
	configPath,
	adType,
	subPlatform,
	deviceConfig,
	isUsingGlobal,
	onUpdate,
	createLocalDeviceConfig
}) => {
	const [css] = useStyletron();

	// Build the path for updating values
	const basePath = [...configPath, adType];
	let deviceConfigPath: string[];
	if (isUsingGlobal) {
		deviceConfigPath = []; // We'll handle global updates differently
	} else if (subPlatform) {
		deviceConfigPath = [...basePath, 'deviceConfig', platform, subPlatform];
	} else {
		deviceConfigPath = [...basePath, 'deviceConfig', platform];
	}

	const enabled = deviceConfig?.enabled ?? false;
	const minAppVersion = deviceConfig?.minAppVersion ?? '';

	return (
		<div
			className={css({
				backgroundColor: '#ffffff',
				border: `2px solid ${enabled ? '#27ae60' : '#e0e0e0'}`,
				borderRadius: '12px',
				padding: '16px',
				boxShadow: enabled ? '0 4px 12px rgba(39, 174, 96, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
				transition: 'all 0.3s ease',
				':hover': {
					transform: 'translateY(-2px)',
					boxShadow: enabled ? '0 8px 24px rgba(39, 174, 96, 0.2)' : '0 8px 24px rgba(0, 0, 0, 0.12)'
				}
			})}>
			{/* Header */}
			<div
				className={css({
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: '12px',
					paddingBottom: '8px',
					borderBottom: '1px solid #f0f0f0'
				})}>
				<div
					className={css({
						display: 'flex',
						alignItems: 'center',
						gap: '8px'
					})}>
					<h5
						className={css({
							fontSize: '1.1rem',
							fontWeight: '700',
							color: enabled ? '#27ae60' : '#6c757d',
							margin: '0'
						})}>
						{title}
					</h5>
				</div>
			</div>

			{/* Enabled Toggle */}
			<div
				className={css({
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: '12px',
					padding: '10px',
					backgroundColor: '#f8f9fa',
					borderRadius: '6px'
				})}>
				<span
					className={css({
						fontSize: '0.9rem',
						fontWeight: '600',
						color: '#495057'
					})}>
					Enabled
				</span>
				<Checkbox
					checked={enabled}
					onChange={(e) => {
						if (isUsingGlobal) {
							const newDeviceConfig = createLocalDeviceConfig(adConfig, platform, subPlatform, {
								enabled: e.target.checked
							});
							onUpdate([...basePath, 'deviceConfig'], newDeviceConfig);
						} else {
							onUpdate([...deviceConfigPath, 'enabled'], e.target.checked);
						}
					}}
					overrides={{
						Checkmark: {
							style: {
								borderColor: '#27ae60'
							}
						}
					}}
				/>
			</div>

			{/* Min App Version */}
			<div>
				<label
					className={css({
						fontSize: '0.9rem',
						fontWeight: '600',
						color: '#495057',
						marginBottom: '8px',
						display: 'block'
					})}>
					Min App Version
				</label>
				<Input
					value={minAppVersion}
					onChange={(e) => {
						const value = e.target.value.trim();
						const numValue = value === '' ? null : parseInt(value, 10) || null;

						if (isUsingGlobal) {
							const newDeviceConfig = createLocalDeviceConfig(adConfig, platform, subPlatform, {
								minAppVersion: numValue
							});
							onUpdate([...basePath, 'deviceConfig'], newDeviceConfig);
						} else {
							onUpdate([...deviceConfigPath, 'minAppVersion'], numValue);
						}
					}}
					placeholder='Enter version'
					overrides={{
						Root: { style: { width: '100%' } },
						Input: {
							style: {
								fontSize: '0.85rem',
								padding: '8px',
								borderRadius: '4px'
							}
						}
					}}
				/>
			</div>
		</div>
	);
};

export default DeviceConfigBox;
